import {Box, Typography} from "@mui/material";
import {CheckCircle, DragIndicator, GpsFixed} from "@mui/icons-material";
import type {DragEvent} from "react";
import {t} from "../dashboard/coach/coachTokens";
import type {MobileMiniWorkout} from "../../api/generated";

type WorkoutState = MobileMiniWorkout["stateType"];

interface ClientWeekChipProps {
  /** 1-based tab index (also the default label: "אימון {index}"). */
  index: number;
  /** The coach-chosen day name, shown instead of "אימון {index}" when set. */
  label?: string;
  setActiveTab: (index: number) => void;
  active?: boolean;
  disable?: boolean;
  /** Legacy completion flag; `stateType === "COMPLETED"` is preferred. */
  isCompleted?: boolean;
  stateType?: WorkoutState;
  draggable?: boolean;
  onDragStart?: (e: DragEvent<HTMLDivElement>) => void;
  onDragOver?: (e: DragEvent<HTMLDivElement>) => void;
  onDragLeave?: (e: DragEvent<HTMLDivElement>) => void;
  onDragEnd?: (e: DragEvent<HTMLDivElement>) => void;
  onDrop?: (e: DragEvent<HTMLDivElement>) => void;
  isDragOver?: boolean;
  isDragging?: boolean;
  isValidDropTarget?: boolean;
  isJustMoved?: boolean;
}

// Pill-styled workout tab, matching the coach dashboard week pills (rounded,
// green-active). Each tab surfaces the workout's live status:
//   active       → green filled pill (you are viewing this workout)
//   COMPLETED    → green outline + ✓ check
//   IN_PROGRESS  → amber outline + pulsing dot ("started, not finished")
//   NOT_STARTED  → neutral outline
// Drag-and-drop target states layer on top and take visual priority.
const ClientWeekChip = ({
  disable,
  index,
  label,
  active,
  setActiveTab,
  isCompleted,
  stateType,
  draggable = false,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDragEnd,
  onDrop,
  isDragOver = false,
  isDragging = false,
  isValidDropTarget = false,
  isJustMoved = false,
}: ClientWeekChipProps) => {

  const canDrag = draggable && !isCompleted;
  // stateType is the source of truth; isCompleted is kept for back-compat.
  const completed = isCompleted || stateType === "COMPLETED";
  const inProgress = !completed && stateType === "IN_PROGRESS";

  // Visual hierarchy (highest priority first):
  //   isDragOver        → bright green, glowing — current drop target
  //   isValidDropTarget → green dashed outline — "you can drop here"
  //   isJustMoved       → green double border — landed here
  //   active            → green filled pill — current tab
  //   completed         → green outline pill
  //   inProgress        → amber outline pill
  //   default           → neutral outline pill
  let backgroundColor;
  let border;
  let boxShadow = "none";
  let textColor;
  let iconColor;

  if (isDragOver) {
    backgroundColor = t.green;
    border = `2px solid ${t.greenDark}`;
    boxShadow = "0 0 0 4px rgba(1,187,167,0.3)";
    textColor = "#FFFFFF";
    iconColor = "#FFFFFF";
  } else if (isValidDropTarget) {
    backgroundColor = t.greenLight;
    border = `2px dashed ${t.green}`;
    textColor = t.greenText;
    iconColor = t.green;
  } else if (isJustMoved) {
    backgroundColor = t.greenLight;
    border = `2px solid ${t.green}`;
    boxShadow = "0 0 0 3px rgba(1,187,167,0.25)";
    textColor = t.greenText;
    iconColor = t.green;
  } else if (active) {
    backgroundColor = t.green;
    border = `1.5px solid ${t.green}`;
    boxShadow = "0 1px 6px rgba(1,187,167,.28)";
    textColor = "#FFFFFF";
    iconColor = "#FFFFFF";
  } else if (completed) {
    backgroundColor = t.white;
    border = `1.5px solid ${t.green}`;
    textColor = t.greenText;
    iconColor = t.green;
  } else if (inProgress) {
    backgroundColor = t.white;
    border = `1.5px solid ${t.amber}`;
    textColor = t.amberText;
    iconColor = t.amber;
  } else {
    backgroundColor = t.white;
    border = `1.5px solid ${t.border}`;
    textColor = t.t2;
    iconColor = t.t3;
  }

  return (
      <Box
        draggable={canDrag}
        onDragStart={canDrag ? (e) => {
          // Firefox requires dataTransfer data to initiate a drag.
          if (e.dataTransfer) {
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', String(index));
          }
          onDragStart && onDragStart(e);
        } : undefined}
        onDragOver={draggable ? (e) => {
          e.preventDefault();
          if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
          onDragOver && onDragOver(e);
        } : undefined}
        onDragLeave={draggable ? (e) => { onDragLeave && onDragLeave(e); } : undefined}
        onDragEnd={draggable ? onDragEnd : undefined}
        onDrop={draggable ? (e) => { e.preventDefault(); e.stopPropagation(); onDrop && onDrop(e); } : undefined}
        title={completed ? "אימון הושלם" : inProgress ? "אימון בתהליך" : active ? "האימון הנוכחי" : undefined}
        sx={{
          cursor: disable ? 'default' : isValidDropTarget ? 'copy' : canDrag ? 'grab' : "pointer",
          backgroundColor,
          border,
          boxShadow,
          borderRadius: "20px",
          padding: { xs: "6px 12px", md: "7px 15px" },
          marginRight: { xs: "3px", md: "4px" },
          marginLeft: { xs: "3px", md: "4px" },
          display: "flex",
          alignItems: "center",
          gap: { xs: "5px", md: "7px" },
          transition: "all 0.15s ease",
          flexShrink: 0,
          opacity: isDragging ? 0.35 : 1,
          transform: isDragOver ? "scale(1.05)" : "scale(1)",
          "&:hover": {
            backgroundColor,
            transform: isDragOver ? "scale(1.05)" : disable ? "none" : "translateY(-1px)",
            boxShadow: isJustMoved || isDragOver
              ? boxShadow
              : disable ? "none" : "0 2px 6px rgba(15,20,40,0.08)"
          },
          // Pulsing dot animation for the in-progress status indicator.
          "@keyframes chipPulse": {
            "0%": { opacity: 1, transform: "scale(1)" },
            "50%": { opacity: 0.4, transform: "scale(0.7)" },
            "100%": { opacity: 1, transform: "scale(1)" },
          },
        }} onClick={() => disable ? {} : setActiveTab(index)}>
        {isValidDropTarget && (
          <GpsFixed sx={{
            fontSize: { xs: "14px", md: "16px" },
            color: iconColor,
          }}/>
        )}
        {!isValidDropTarget && draggable && !completed && (
          <DragIndicator sx={{
            fontSize: { xs: "14px", md: "16px" },
            color: active ? "rgba(255,255,255,0.7)" : "#94A3B8",
            cursor: 'grab',
          }}/>
        )}

        <Typography sx={{
          color: textColor,
          fontSize: { xs: "12px", md: "13.5px" },
          fontWeight: active || isValidDropTarget || isDragOver ? 700 : 600,
          lineHeight: "1",
          whiteSpace: "nowrap",
          maxWidth: { xs: "120px", md: "180px" },
          overflow: "hidden",
          textOverflow: "ellipsis",
        }} title={label || undefined}>
          {isDragOver ? `שחרר כאן → אימון ${index}` : isValidDropTarget ? `מקם כאן (${index})` : (label || `אימון ${index}`)}
        </Typography>

        {/* Status indicator (hidden while dragging) */}
        {!isValidDropTarget && !isDragOver && completed && (
          <CheckCircle sx={{
            fontSize: { xs: "13px", md: "15px" },
            color: iconColor,
          }}/>
        )}
        {!isValidDropTarget && !isDragOver && inProgress && (
          <Box sx={{
            width: { xs: 7, md: 8 },
            height: { xs: 7, md: 8 },
            borderRadius: "50%",
            // White on the green active fill, amber otherwise — keeps the
            // in-progress dot visible even when this is the selected tab.
            backgroundColor: active ? "#FFFFFF" : t.amber,
            animation: "chipPulse 1.4s ease-in-out infinite",
            flexShrink: 0,
          }}/>
        )}
      </Box>
  );
};

export default ClientWeekChip;
